import { useRef, useState, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import Webcam from 'react-webcam'
import { Spinner } from 'react-bootstrap'
import CustomModal from '../customModal'
import {
  InterviewButtonsContainer,
  InterviewVideoPreviewContainer,
} from '../styled'
import { RecordingIndicator } from '../mobileVideoInterview/styled'
import Mic from '../../assets/svgs/mic'
import Stop from '../../assets/svgs/stopCircle'
import Resume from '../../assets/svgs/Resume'
import Pause from '../../assets/svgs/Pause'
import theme from '../../constants/theme'
import PrimaryButton from '../buttons/PrimaryButton'
import apiHMSVideoControl from '../../services/api/hmsVideoControl'
import { decodeInterviewToken } from '../../utilities/tokenUtils'

export default function RecordingSectionThirdParty({
  recordingStatus,
  setRecordingStatus,
  isLoading,
  questionId,
  recordingTime = 0,
  maxRecordingTime = null,
  isCoverLetter = false,
  onRecordingStart = null,
}) {
  const { t } = useTranslation()
  const message = t('interview.editResponseQuestion')
  const { interviewToken } = useParams()
  const interviewVersionId =
    decodeInterviewToken(interviewToken).interview_version_id
  const [show, setShow] = useState(false)
  const webcamRef = useRef(null)
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)
  const [isBlinking, setIsBlinking] = useState(false)

  useEffect(() => {
    if (recordingStatus === 'recording') {
      const interval = setInterval(() => {
        setIsBlinking((prev) => !prev)
      }, 800)
      return () => clearInterval(interval)
    }
    setIsBlinking(false)
    return undefined
  }, [recordingStatus])

  useEffect(() => {
    if (recordingStatus === 'maxTimeReached') {
      stopRecording()
    }
  }, [recordingStatus])

  const roomId = localStorage.getItem('room_id')
  const startRecording = useCallback(async () => {
    try {
      setRecordingStatus('loading')
      if (onRecordingStart) {
        onRecordingStart()
      }
      const response = await apiHMSVideoControl.startRecording(
        questionId,
        roomId,
        interviewVersionId,
      )
      if (response.success) {
        setRecordingStatus('recording')
      }
    } catch (_error) {
      if (_error?.response?.data?.message.includes('beam already started'))
        setRecordingStatus('recording')
    }
  }, [questionId, roomId, onRecordingStart])

  const pauseRecording = useCallback(async () => {
    try {
      setRecordingStatus('pausing')
      const response = await apiHMSVideoControl.pauseRecording(roomId)

      if (response.success) {
        setRecordingStatus('paused')
      }
    } catch (_error) {
      if (_error?.response?.data?.message.includes('beam already paused')) {
        setRecordingStatus('paused')
      }
    }
  }, [])

  const resumeRecording = useCallback(async () => {
    try {
      const response = await apiHMSVideoControl.resumeRecording(roomId)

      if (response.success) {
        setRecordingStatus('recording')
      }
    } catch (_error) {
      if (_error?.response?.data?.message.includes('beam already started'))
        setRecordingStatus('recording')
    }
  }, [])

  const stopRecording = useCallback(async () => {
    try {
      setRecordingStatus('stopping')
      const response = await apiHMSVideoControl.stopRecording(
        roomId,
        interviewVersionId,
      )

      if (response.success) {
        setRecordingStatus('stopped')
      }
    } catch {
      setRecordingStatus('stopped')
    }
  }, [roomId, interviewVersionId])

  const editRecord = async () => {
    setShow(false)
    setRecordingStatus('loading')
    startRecording()
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs < 10 ? `0${secs}` : secs}`
  }

  const getRemainingTime = () => {
    if (!isCoverLetter || !maxRecordingTime) return null
    return maxRecordingTime - recordingTime
  }

  const isNearTimeLimit = () => {
    const remaining = getRemainingTime()
    return remaining !== null && remaining <= 30 && remaining > 0
  }

  const getRecordingButtonDisabled = () => {
    return recordingStatus === 'loading'
  }

  return (
    <>
      <CustomModal
        show={show}
        handleClose={handleClose}
        handleSubmit={editRecord}
        message={message}
      />
      <InterviewVideoPreviewContainer>
        <div style={{ height: '480px', width: '100%', position: 'relative' }}>
          <Webcam audio muted ref={webcamRef} height="100%" width="100%" />
          {recordingStatus === 'recording' && (
            <div
              style={{
                position: 'absolute',
                top: '15px',
                left: '15px',
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(8px)',
                borderRadius: '12px',
                padding: '5px 10px',
              }}
            >
              <RecordingIndicator isBlinking={isBlinking}>
                REC
              </RecordingIndicator>
            </div>
          )}
          {isCoverLetter && recordingStatus === 'recording' && (
            <div
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                zIndex: 10,
                backgroundColor: isNearTimeLimit()
                  ? 'rgba(255, 0, 0, 0.7)'
                  : 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(8px)',
                borderRadius: '12px',
                padding: '5px 10px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {formatTime(recordingTime)} / {formatTime(maxRecordingTime)}
            </div>
          )}
        </div>
        <InterviewButtonsContainer>
          <PrimaryButton
            color={
              recordingStatus !== 'stopped' || isLoading
                ? theme.talentinoDarkerGray
                : theme.talentinoSpecialBlue
            }
            backgroundcolor={theme.talentinoWhiteBlue}
            hovercolor={theme.talentinoDarkerWhiteBlue}
            disabled={recordingStatus !== 'stopped' || isLoading}
            onClick={handleShow}
          >
            {t('interview.changeResponse')}
          </PrimaryButton>
          {(recordingStatus === 'notAnswered' ||
            recordingStatus === 'loading') && (
            <PrimaryButton
              backgroundcolor={
                recordingStatus === 'loading' || getRecordingButtonDisabled()
                  ? theme.talentinoDarkerBlue
                  : theme.talentinoBlue
              }
              disabled={
                recordingStatus === 'loading' || getRecordingButtonDisabled()
              }
              onClick={startRecording}
            >
              {recordingStatus === 'notAnswered' && (
                <Mic
                  width={18}
                  height={18}
                  color="#FFFFFF"
                  style={{ marginRight: '1px' }}
                />
              )}
              {recordingStatus === 'loading' && (
                <Spinner
                  style={{ marginRight: '5px' }}
                  animation="border"
                  variant="secondary"
                  size="sm"
                />
              )}
              {t('interview.startRecording')}
            </PrimaryButton>
          )}
          {recordingStatus === 'paused' && (
            <PrimaryButton
              backgroundcolor={
                recordingStatus === 'loading'
                  ? theme.talentinoDarkerTeal
                  : theme.talentinoTeal
              }
              style={{ fontSize: '11px' }}
              disabled={recordingStatus === 'loading'}
              onClick={resumeRecording}
            >
              {recordingStatus === 'paused' && (
                <Resume width={18} height={18} color="#FFFFFF" />
              )}
              {recordingStatus === 'loading' && (
                <Spinner
                  style={{ marginRight: '1px' }}
                  animation="border"
                  variant="secondary"
                  size="sm"
                />
              )}
              {t('interview.resumeRecording')}
            </PrimaryButton>
          )}
          {(recordingStatus === 'recording' ||
            recordingStatus === 'pausing') && (
            <PrimaryButton
              backgroundcolor={
                recordingStatus === 'paused'
                  ? theme.talentinoDarkerOrange
                  : theme.talentinoOrange
              }
              hovercolor={theme.talentinoDarkerOrange}
              disabled={recordingStatus === 'pausing'}
              onClick={pauseRecording}
            >
              {(recordingStatus === 'pausing' ||
                recordingStatus === 'recording') && (
                <Pause
                  width={18}
                  height={18}
                  color="#FFFFFF"
                  style={{ marginRight: '1px' }}
                />
              )}
              {recordingStatus !== 'pausing' &&
                recordingStatus !== 'recording' && (
                  <Spinner
                    style={{ marginRight: '1px' }}
                    animation="border"
                    variant="secondary"
                    size="sm"
                  />
                )}
              {t('interview.pauseRecording')}
            </PrimaryButton>
          )}
          {(recordingStatus === 'recording' ||
            recordingStatus === 'stopping') && (
            <PrimaryButton
              backgroundcolor={
                recordingStatus === 'stopping'
                  ? theme.talentinoDarkerRed
                  : theme.talentinoRed
              }
              hovercolor={theme.talentinoDarkerRed}
              disabled={recordingStatus === 'stopping'}
              onClick={stopRecording}
            >
              {recordingStatus === 'recording' && (
                <Stop
                  width={18}
                  height={18}
                  color="#FFFFFF"
                  style={{ marginRight: '1px' }}
                />
              )}
              {recordingStatus !== 'recording' &&
                recordingStatus !== 'stopped' && (
                  <Spinner
                    style={{ marginRight: '1px' }}
                    animation="border"
                    variant="secondary"
                    size="sm"
                  />
                )}
              {t('interview.stopRecording')}
            </PrimaryButton>
          )}
        </InterviewButtonsContainer>
      </InterviewVideoPreviewContainer>
    </>
  )
}

RecordingSectionThirdParty.propTypes = {
  questionId: PropTypes.number.isRequired,
  recordingStatus: PropTypes.string.isRequired,
  setRecordingStatus: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  recordingTime: PropTypes.number,
  maxRecordingTime: PropTypes.number,
  isCoverLetter: PropTypes.bool,
  onRecordingStart: PropTypes.func,
}
